<template>
  <div id="app" class="max-w-md px-6 py-6 mx-auto bg-white rounded-md shadow-md">
    <ValidationObserver v-slot="{ handleSubmit }" v-if="screen === 0">
      <form autocomplete="off" @submit.prevent="handleSubmit(onSubmit)">
        <input type="hidden" name="booking" v-model="form.booking" ref="booking" />
        <div class="grid grid-cols-2">
          <validation-provider rules="required" v-slot="{ errors }">
            <div class="mb-3 mr-2">
              <label for="full_name" class="sr-only">Full Name</label>
              <div class="relative mt-1 rounded-md shadow-sm">
                <input
                  type="text"
                  name="full_name"
                  id="full_name"
                  v-model="form.full_name"
                  class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                  :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                  placeholder="Full Name"
                />
                <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5 text-gray-400"
                  >
                    <path
                      d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </validation-provider>
          <validation-provider rules="required" v-slot="{ errors }">
            <div class="mb-3 ml-2">
              <label for="email" class="sr-only">Email</label>
              <div class="relative mt-1 rounded-md shadow-sm">
                <input
                  type="email"
                  name="email"
                  id="email"
                  v-model="form.email"
                  class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                  :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                  placeholder="Your Email"
                />
                <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5 text-gray-400"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M14.243 5.757a6 6 0 10-.986 9.284 1 1 0 111.087 1.678A8 8 0 1118 10a3 3 0 01-4.8 2.401A4 4 0 1114 10a1 1 0 102 0c0-1.537-.586-3.07-1.757-4.243zM12 10a2 2 0 10-4 0 2 2 0 004 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </validation-provider>

          <validation-provider rules="required" v-slot="{ errors }">
            <div class="mb-3 mr-2">
              <label for="phone" class="sr-only">Phone number</label>
              <div class="relative mt-1 rounded-md shadow-sm">
                <input
                  type="text"
                  name="phone"
                  id="phone"
                  v-model="form.phone"
                  class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                  :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                  placeholder="5555-123-456"
                />
                <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5 text-gray-400"
                  >
                    <path
                      d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </validation-provider>
          <validation-provider rules="required|min_value:1" v-slot="{ errors }">
            <div class="mb-3 ml-2">
              <label for="project_type" class="sr-only">Project Type</label>
              <select
                id="project_type"
                v-model="form.project_type"
                name="project_type"
                class="block w-full py-2 pl-3 pr-10 mt-1 text-base border rounded-md focus:outline-none focus:ring-acquira-blue focus:border-acquira-blue sm:text-sm"
                :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
              >
                <option :value="service.id" v-for="service in services" :key="service.id">{{ service.label }}</option>
              </select>
            </div>
          </validation-provider>
        </div>

        <div class="mb-5">
          <label for="description" class="sr-only">Project description (optional)</label>
          <textarea
            rows="3"
            name="description"
            id="description"
            v-model="form.project_description"
            class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border border-gray-300 rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
            placeholder="Project Description (optional)"
          />
        </div>

        <div class="space-y-3">
          <div class="pb-2 px-2 font-bold">
            <span class="flex flex-col flex-grow" id="availability-label">
              <span class="text-sm text-gray-700"
                >If you'd like to request an appointment, please choose the date and time preferable for you:</span
              >
            </span>
          </div>
          <div class="grid grid-cols-2">
            <validation-provider rules="" v-slot="{ errors }">
              <div>
                <date-picker
                  v-model="form.date"
                  format="D MMM YYYY"
                  value-type="format"
                  :clearable="false"
                  :default-value="new Date()"
                  :disabled-date="disabledDates"
                  type="date"
                >
                  <template v-slot:input="slotProps">
                    <div>
                      <label for="date" class="sr-only">Date</label>
                      <div class="relative mt-1 mr-2 rounded-md shadow-sm">
                        <input
                          type="text"
                          name="date"
                          class="block w-full px-4 py-2 pr-10 text-gray-700 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                          :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                          placeholder="Date"
                          v-bind="slotProps.props"
                          v-on="slotProps.events"
                        />
                        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            class="w-5 h-5 text-gray-400"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-slot:icon-calendar><span></span></template>
                </date-picker>
              </div>
            </validation-provider>
            <validation-provider rules="" v-slot="{ errors }">
              <div class="ml-2">
                <label for="project_type" class="sr-only">Time</label>
                <select
                  id="project_type"
                  name="project_type"
                  class="block w-full py-2 pl-3 pr-10 mt-1 text-base text-gray-700 border rounded-md focus:outline-none focus:ring-brand-blue focus:border-brand-blue sm:text-sm"
                  :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                  v-model="form.time"
                >
                  <option :value="time.id" v-for="time in times" :key="time.id">{{ time.label }}</option>
                </select>
              </div>
            </validation-provider>
            <validation-provider rules="" v-slot="{ errors }">
              <div class="my-3 mr-2">
                <label for="address" class="sr-only">Address</label>
                <div class="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    name="address"
                    id="address"
                    v-model="form.address"
                    class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                    :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                    placeholder="Full Address"
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5 text-gray-400"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </validation-provider>
            <validation-provider rules="" v-slot="{ errors }">
              <div class="mt-3 ml-2">
                <label for="zip" class="sr-only">Zip Code</label>
                <div class="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    name="zip"
                    id="zip"
                    v-model="form.zip"
                    class="block w-full px-4 py-2 pr-10 placeholder-gray-700 bg-white border rounded-md focus:ring-brand-blue focus:border-brand-blue sm:text-sm focus:outline-none"
                    :class="[errors[0] ? 'border-red-500' : 'border-gray-300']"
                    placeholder="Zip Code"
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5 text-gray-400"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </validation-provider>
          </div>
        </div>

        <div>
          <button
            type="submit"
            class="w-full p-0 py-3 mt-3 text-base font-medium text-white border border-transparent rounded-md shadow-sm bg-brand-blue hover:bg-brand-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue"
          >
            <div class="inline-flex items-center">
              Request an appointment
              <svg
                class="w-5 h-5 ml-3 -mr-1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
          </button>
          <div class="text-center text-sm font-italic mt-2 text-gray-600">
            Free in-home consultation.
          </div>
        </div>
      </form>
    </ValidationObserver>
    <div v-if="screen === 1">
      <div class="flex flex-col items-center justify-center">
        <svg
          class="w-24 h-24 text-green-500 stroke-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="px-12 mt-5 text-xl font-bold text-center text-gray-700">
          Thank you, we are saving your information!
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import DatePicker from 'vue2-datepicker'
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import 'vue2-datepicker/index.css'
  import service from './services'

  export default {
    name: 'App',
    components: { DatePicker, ValidationProvider, ValidationObserver },
    data() {
      return {
        screen: 0,
        date: null,
        isLoading: false,
        form: {
          project_type: 0,
          time: 0,
        },
        services: [
          { id: 0, label: 'Project Type' },
          { id: 1, label: 'Filter Change for RO' },
          { id: 2, label: 'Water Softener Maintenance' },
          { id: 3, label: 'Water Heater Maintenance' },
          { id: 4, label: 'AC/Heating Maintenance' },
          { id: 5, label: 'AC Estimates' },
          { id: 6, label: 'Plumbing Estimates' },
          { id: 7, label: 'Duct Cleaning' },
          { id: 8, label: 'Other' },
        ],
        times: [
          { id: 0, label: 'Time' },
          { id: 1, label: '8am - Noon' },
          { id: 2, label: '1pm - 5pm' },
        ],
      }
    },
    mounted() {
      console.log('Web Booking v2.0.0')
    },
    methods: {
      disabledDates(date) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        today.setDate(today.getDate() + 2)
        const day = date.getDay()

        return date < today || day === 0
      },
      onSubmit() {
        this.screen++
        const data = { ...this.form }
        data.project_type = this.services[this.form.project_type].label
        data.time = this.times[this.form.time].label
        data.request_appointment = this.appointmentActive
        service.storeRecord(data)

        setInterval(() => {
          window.location.href = 'https://americanhomewater.com/thank-you'
        }, 2000)
      },
    },
  }
</script>

<style>
  .mx-datepicker {
    width: 100%;
    max-width: 100%;
  }
  .mx-input {
    height: auto !important;
    color: inherit;
    line-height: inherit !important;
  }
  .mx-calendar-content table {
    border: 0 none transparent;
    text-align: center;
  }
  .mx-calendar-content table th {
    border: 0 none transparent;
    text-align: center;
  }
  .mx-calendar-content table tr {
    border: 0 none transparent;
    text-align: center;
  }
  .mx-calendar-content table td {
    border: 0 none transparent;
    text-align: center;
  }
  select {
    background-image: url(data:image/svg+xml;charset=utf-8;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIGZpbGw9J25vbmUnIHZpZXdCb3g9JzAgMCAyMCAyMCc+PHBhdGggc3Ryb2tlPScjNzE3MTdhJyBzdHJva2UtbGluZWNhcD0ncm91bmQnIHN0cm9rZS1saW5lam9pbj0ncm91bmQnIHN0cm9rZS13aWR0aD0nMS41JyBkPSdNNiA4bDQgNCA0LTQnLz48L3N2Zz4=);
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    background-color: white;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    -webkit-appearance: none;
    -moz-appearance: none;
    text-indent: 1px;
    text-overflow: '';
  }

  input:disabled,
  select:disabled {
    background: rgba(0, 0, 0, 0.12) !important;
  }
</style>
